import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  Inject,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormBuilder } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { CONSTANTS_TOKEN } from '../../../config';
import { PortalService } from '../../../services/portal.service';
import { UtilsService } from '../../../services/utils.service';
import { DocumentViewerComponent } from '../document-viewer/document-viewer.component';
import { DateRangePickerComponent } from '../../date-range-picker/date-range-picker.component';
import { FileIconComponent, FileIconType } from '../file-icon/file-icon.component';
import { TableComponent } from '../../table/table.component';
import { SnackBar, TableActionType } from '../../../enums';
import { DocumentItem, MIME_TYPE_ICONS, EXT_ICONS } from '../document.model';
import { GrantAccessDialogComponent } from '../grant-access-dialog/grant-access-dialog.component';
import { AreaOfInterestDialogComponent } from '../../area-of-interest-dialog/area-of-interest-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ViewModeToggleComponent, ViewMode } from '../view-mode-toggle/view-mode-toggle.component';

@Component({
  selector: 'educare-folder-contents',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatSnackBarModule,
    TranslateModule,
    DocumentViewerComponent,
    DateRangePickerComponent,
    FileIconComponent,
    TableComponent,
    ViewModeToggleComponent
  ],
  templateUrl: './folder-contents.component.html',
  styleUrls: ['./folder-contents.component.scss']
})
export class FolderContentsComponent implements OnInit, OnChanges {
  @Input() folderId!: string;
  @Input() folderName = 'Folder Contents';
  @Input() pageSize = 20;
  @Input() showPagination = true;
  @Input() viewMode: ViewMode = 'list';
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() itemClick = new EventEmitter<DocumentItem>();
  @Output() back = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<DocumentItem[]>();

  items: DocumentItem[] = [];
  paginatedItems: DocumentItem[] = [];
  loading = false;
  currentPage = 0;
  totalItems = 0;
  showViewer = false;
  selectedDocument: DocumentItem | null = null;
  selectedItems: DocumentItem[] = [];
  selectAll = false;
  filterForm!: FormGroup;
  filters: any = {
    filter: {},
    pagination: {
      limit: 10,
      page: 1
    },
    sort: undefined
  };

  fileTypes = [
    { id: 'pdf', name: this.utils.translation('fileType.pdf') },
    { id: 'docx', name: this.utils.translation('fileType.docx') },
    { id: 'xlsx', name: this.utils.translation('fileType.xlsx') },
    { id: 'doc', name: this.utils.translation('fileType.doc') },
    { id: 'xls', name: this.utils.translation('fileType.xls') },
    { id: 'csv', name: this.utils.translation('fileType.csv') }
  ];

  sortOptions = [
    { id: 'name', name: this.utils.translation('sortingFilter.name') },
    { id: 'modified_date', name: this.utils.translation('sortingFilter.dateModified') },
    { id: 'file_type', name: this.utils.translation('sortingFilter.fileType') }
  ];

  sortOrder = [
    { id: 'asc', name: this.utils.translation('sortingFilter.asc') },
    { id: 'desc', name: this.utils.translation('sortingFilter.desc') }
  ];
  private readonly dialog = inject(MatDialog);

  private static readonly AOI_DIALOG_CONFIG = {
    width: '420px',
    maxWidth: '95vw',
    panelClass: 'area-of-interest-dialog-panel'
  } as const;

  get enableSelection(): boolean {
    return this.constants?.app?.portalType === 'admin';
  }

  get tableDataSource() {
    return {
      items: this.paginatedItems,
      count: this.totalItems,
      page: this.currentPage + 1,
      limit: this.pageSize
    };
  }

  get tableColumns() {
    const baseColumns: any[] = [
      {
        name: 'name',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.title'),
        onClick: (_$event: any, element: DocumentItem) => {
          this.onItemClick(element);
        }
      },
      {
        name: 'updatedDate',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.updatedDate')
      },
      {
        name: 'updatedBy',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.updatedBy')
      }
    ];
    if (this.enableSelection) {
      baseColumns.push({
        name: 'actions',
        title: this.utils.translation('labels.actions'),
        type: TableActionType.IconButton,
        stickyEnd: true,
        actions: [
          {
            id: 'areaOfInterest',
            iconClass: 'das-bookmark_heart fs-4',
            tooltip: this.utils.translation('common.areaOfInterest.label'),
            disabled: () => this.selectedItems.length > 0
          },
          {
            id: 'grantAccess',
            iconClass: 'das-article_person fs-4',
            tooltip: this.utils.translation('common.grantAccess'),
            disabled: () => this.selectedItems.length > 0
          },
          {
            id: 'revokeAccess',
            iconClass: 'das-undo fs-4',
            tooltip: this.utils.translation('common.revokeAccess'),
            disabled: () => this.selectedItems.length > 0
          }
        ],
        onClick: (_$event: any, element: DocumentItem, actionId: string) => {
          if (actionId === 'areaOfInterest') {
            this.openAreaOfInterestDialog([element.id]);
          } else {
            this.openGrantAccessDialog(actionId === 'revokeAccess', element);
          }
        }
      });
    }

    return baseColumns;
  }

  get tableSelectionConfig() {
    if (!this.enableSelection) {
      return undefined;
    }

    return {
      enabled: true,
      selectedItems: this.selectedItems.map((item) => item.id),
      selectAll: this.selectAll,
      selectionKey: 'id',
      totalCount: this.totalItems,
      selectedCount: this.selectedItems.length
    };
  }

  get hasResults(): boolean {
    return this.items.length > 0;
  }

  get hasSelectedItems(): boolean {
    return this.selectedItems.length > 0;
  }

  constructor(
    @Inject(CONSTANTS_TOKEN) private constants: any,
    private portalService: PortalService,
    private utils: UtilsService,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      file_type: [''],
      uploaded_by: [''],
      status: [''],
      start: [null],
      end: [null],
      sort_by: [''],
      sort_order: ['']
    });
  }

  ngOnInit() {
    this.loadFolderContents();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['folderId'] && !changes['folderId'].firstChange) {
      this.showViewer = false;
      this.selectedDocument = null;
      this.loadFolderContents();
    }
  }

  loadFolderContents() {
    if (!this.portalService || !this.folderId) {
      return;
    }

    this.selectedItems = [];
    this.selectAll = false;
    this.loading = true;

    const params = new URLSearchParams({
      page: (this.currentPage + 1).toString(),
      limit: this.pageSize.toString()
    });

    if (this.filters.filter) {
      Object.keys(this.filters.filter).forEach((key) => {
        const value = this.filters.filter[key];
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
    }

    const queryString = params.toString();
    const url = `${this.utils.getEncryptedUrls(this.constants.api.privateEndpoints.folderContent)}/${this.folderId}/contents?${queryString}`;

    this.portalService.get(url).subscribe({
      next: (response: any) => {
        const contents = response?.data?.items || [];
        this.items = contents.map((item: any) => this.transformItem(item));
        this.paginatedItems = this.items;
        this.totalItems = response?.data?.total || contents.length;
        this.loading = false;
        this.updateSelectAllState();
      },
      error: (error: any) => {
        this.utils.openSnackBar(error.error.message, 'Close', SnackBar.Error);
        this.loading = false;
        this.items = [];
        this.paginatedItems = [];
        this.totalItems = 0;
      }
    });
  }

  private transformItem(item: any): DocumentItem {
    const fileName = item.name || item.title || this.utils.translation('common.untitled');
    const fileExtension = this.getFileExtension(fileName);
    const itemType = item.type || (item.folder ? 'folder' : 'file');
    const updatedDate = item.lastModifiedDateTime ? new Date(item.lastModifiedDateTime) : new Date();

    return {
      id: item.id || item.driveItem?.id,
      name: fileName,
      type: itemType,
      updatedDate: this.formatDate(updatedDate),
      updatedBy: item.lastModifiedBy || this.utils.translation('common.unknown'),
      fileCount: itemType === 'folder' ? (item.childCount ?? 0) : undefined,
      webUrl: item.webUrl,
      mimeType: item.mimeType,
      fileExtension: fileExtension
    };
  }

  private getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  getFileIcon(item: DocumentItem): FileIconType {
    if (item.type === 'folder') {
      return 'folder';
    }
    if (item.mimeType) {
      const icon = MIME_TYPE_ICONS[item.mimeType.toLowerCase()];
      if (icon) {
        return icon;
      }
    }
    if (item.fileExtension) {
      const icon = EXT_ICONS[item.fileExtension.toLowerCase()];
      if (icon) {
        return icon;
      }
    }
    return 'doc';
  }

  formatDate(date: Date | string): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  }

  onViewModeToggle(mode: ViewMode) {
    this.viewMode = mode;
    this.viewModeChange.emit(mode);
    this.currentPage = 0;
    this.loadFolderContents();
  }

  onItemClick(item: DocumentItem) {
    if (item.type === 'folder') {
      this.itemClick.emit(item);
    } else {
      this.selectedDocument = item;
      this.showViewer = true;
    }
  }

  onViewerBack() {
    this.showViewer = false;
    this.selectedDocument = null;
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadFolderContents();
  }

  onBack() {
    this.back.emit();
  }

  trackByFn(_index: number, item: DocumentItem): string {
    return item.id;
  }

  toggleItemSelection(item: DocumentItem, checked: boolean): void {
    if (checked) {
      if (!this.selectedItems.some((selected) => selected.id === item.id)) {
        this.selectedItems.push(item);
      }
    } else {
      this.selectedItems = this.selectedItems.filter((selected) => selected.id !== item.id);
    }
    this.updateSelectAllState();
    this.selectionChange.emit(this.selectedItems);
  }

  isItemSelected(item: DocumentItem): boolean {
    return this.selectedItems.some((selected) => selected.id === item.id);
  }

  private updateSelectAllState(): void {
    const itemsOnPage = this.paginatedItems;
    const selectedOnPage = itemsOnPage.filter((item) => this.isItemSelected(item));
    this.selectAll = itemsOnPage.length > 0 && selectedOnPage.length === itemsOnPage.length;
  }

  clearSelection(): void {
    this.selectedItems = [];
    this.selectAll = false;
    this.selectionChange.emit(this.selectedItems);
  }

  openGrantAccessDialog(isRevokeMode: boolean, document?: DocumentItem): void {
    const selectedDocuments = document ? [document] : this.selectedItems;
    const shouldClearSelection = !document;

    const dialogRef = this.dialog.open(GrantAccessDialogComponent, {
      data: {
        isRevokeMode,
        selectedDocuments,
        source: 'document-browser'
      },
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(() => {
      if (shouldClearSelection) {
        this.clearSelection();
      }
    });
  }

  openAreaOfInterestDialog(documentIds?: string[]): void {
    const ids = documentIds ?? this.selectedItems.map((d) => d.id);
    const isBulk = !documentIds;

    this.dialog
      .open(AreaOfInterestDialogComponent, {
        ...FolderContentsComponent.AOI_DIALOG_CONFIG,
        data: { initialSelected: [], documentIds: ids }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.assignSuccess) {
          isBulk ? this.clearSelection() : this.loadFolderContents();
        }
      });
  }

  onTableSelectionChange(selectedIds: string[]): void {
    this.selectedItems = this.items.filter((item) => selectedIds.includes(item.id));
    this.updateSelectAllState();
    this.selectionChange.emit(this.selectedItems);
  }

  onTableSelectAllAcrossPages(selectAll: boolean): void {
    this.selectAll = selectAll;
    if (selectAll) {
      this.paginatedItems.forEach((item) => {
        if (!this.selectedItems.some((selected) => selected.id === item.id)) {
          this.selectedItems.push(item);
        }
      });
    } else {
      const idsOnPage = this.paginatedItems.map((item) => item.id);
      this.selectedItems = this.selectedItems.filter((item) => !idsOnPage.includes(item.id));
    }
    this.selectionChange.emit(this.selectedItems);
  }

  onFilterChange(event: Event, filterType: string) {
    const filter = (event.target as HTMLInputElement)?.value;
    if (filterType === 'sort_by') {
      if (filter === 'asc' || filter === 'desc') {
        this.filters.filter['sort_order'] = filter;
      } else {
        this.filters.filter['sort_by'] = filter;
      }
    } else {
      this.filters.filter[filterType] = filter;
    }
    this.filters.pagination.page = 1;
    this.currentPage = 0;
    this.loadFolderContents();
  }

  applyDateFilter(dateRange: { start: string; end: string }): void {
    if (dateRange.start && dateRange.end) {
      this.filters.filter.start_date = dateRange.start;
      this.filters.filter.end_date = dateRange.end;
    } else {
      delete this.filters.filter.start_date;
      delete this.filters.filter.end_date;
    }
    this.filters.pagination.page = 1;
    this.currentPage = 0;
    this.loadFolderContents();
  }
}
